"use client";

import { useState } from "react";
import { isLetterAvatarUrl } from "@/lib/avatar";
import { cn } from "./cn";

const SIZE_CLASSES = {
  /** For a face that is decoration beside a sentence that already names the
   *  person — the notification list's stack (OSO-605), not a byline. */
  xs: "w-6 h-6 text-[0.625rem]",
  sm: "w-8 h-8 text-xs",
  md: "w-10 h-10 text-sm",
  lg: "w-12 h-12 text-base",
  full: "w-full h-full text-2xl",
} as const;

/** Deterministic identity palette — vivid by design, independent of theme. */
const COLORS = [
  "bg-red-500",
  "bg-orange-500",
  "bg-amber-500",
  "bg-emerald-500",
  "bg-teal-500",
  "bg-cyan-500",
  "bg-blue-500",
  "bg-indigo-500",
  "bg-violet-500",
  "bg-purple-500",
  "bg-pink-500",
  "bg-rose-500",
];

function getInitials(name: string): string {
  return name.trim()[0]?.toUpperCase() ?? "?";
}

function getColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return COLORS[Math.abs(hash) % COLORS.length];
}

/**
 * The fallback avatar is a single letter at display size, which makes it a
 * piece of brand typography rather than body text — so it is set in Neureal,
 * through the `--font-display` token (`font-display`), the same way headings
 * are. It used to render in Inter, the body face (OSO-425).
 *
 * No weight utility: Neureal ships one face (400, `public/fonts/`), and
 * asking for `font-semibold` only got the browser to fake one.
 */
function InitialsAvatar({
  name,
  sizeClass,
}: {
  name: string;
  sizeClass: string;
}) {
  return (
    <div
      className={cn(
        "rounded-full flex items-center justify-center font-display text-white shrink-0",
        sizeClass,
        getColor(name),
      )}
    >
      {getInitials(name)}
    </div>
  );
}

export interface AvatarProps {
  /** Photo URL. Falls back to coloured initials when missing or broken. */
  url?: string | null;
  /** Display name — drives initials and the deterministic fallback colour. */
  name: string;
  size?: keyof typeof SIZE_CLASSES;
}

/** User avatar with a deterministic coloured-initials fallback. */
export function Avatar({ url, name, size = "md" }: AvatarProps) {
  const [failed, setFailed] = useState(false);
  const sizeClass = SIZE_CLASSES[size];

  // A letter avatar means "no picture uploaded", so we draw our own initials
  // rather than fetching the server's PNG. `lib/avatar` owns that test — it
  // used to be written out here AND in ProfileCompletionNudge, and the copy
  // here was the stale one (OSO-453).
  // (`url &&` is what narrows the type for `src` below; the predicate alone
  // cannot, since it answers true for a missing URL too.)
  if (url && !failed && !isLetterAvatarUrl(url)) {
    return (
      <img
        src={url}
        alt={name}
        className={cn("rounded-full object-cover shrink-0", sizeClass)}
        onError={() => setFailed(true)}
      />
    );
  }

  return <InitialsAvatar name={name} sizeClass={sizeClass} />;
}
